/* Copyright 2016-2022
   Kabandy
 */

import { ApiDataModel } from "../ApiDataModel";
import { ApiRequestHandler, getApiKey } from "../ApiWeb";
import { QuantityDataModel } from "../DataModel";

type IApiQuantity = ApiDataModel.IQuantity;

let _rootDiv: HTMLDivElement | undefined = undefined;
let _currentQuantitiesHtmlListElement: HTMLUListElement | undefined = undefined;
let _currentSelectedQuantityHtmlDivElement: HTMLDivElement | undefined = undefined;

let _accessToken: string = "";
let _onQuantitySelected: Function | undefined = undefined;
let _currentQuantities: IApiQuantity[] = [];

export module UIQuantity {
    export function Bind(accessToken: string, workspaceId: string, onQuantitySelected: Function): void {
        _accessToken = accessToken;
        _onQuantitySelected = onQuantitySelected;

        _rootDiv = _InitializeUI( accessToken, workspaceId, _onQuantitySelected);
        document.body.appendChild(_rootDiv);
    }
    export function Unbind() {
        if (_rootDiv) {
            _rootDiv.remove();
        }

        _currentQuantities = [];

        if (_currentQuantitiesHtmlListElement) {
            _currentQuantitiesHtmlListElement = undefined;
        }

        if (_currentSelectedQuantityHtmlDivElement) {
            _currentSelectedQuantityHtmlDivElement.style.fontWeight = "";
            _currentSelectedQuantityHtmlDivElement.style.textDecoration = "";
        }
    }

    export async function OnQuantityCreated( accessToken: string, workspaceId: string, quantity : IApiQuantity ) : Promise<void>
    {
        _currentQuantities = await _FetchQuantities( accessToken, workspaceId );
        _currentQuantitiesHtmlListElement = _RefreshUIQuantities(_currentQuantitiesHtmlListElement, _currentQuantities);
    }

    function _InitializeUI( accessToken: string, workspaceId: string, onOpenQuantity: Function): HTMLDivElement {
        let rootDiv = document.createElement("div");
        rootDiv.id = "ui-quantities-root-div";

        // title
        let titleElement = document.createElement("h2");
        titleElement.innerText = "Quantities";
        rootDiv.appendChild(titleElement);

        // get button
        let getButton = document.createElement("button");
        getButton.id = "ui-quantities-get-button";
        getButton.textContent = "Get Quantities";
        rootDiv.appendChild(getButton);
        rootDiv.appendChild(document.createElement("br"));
        getButton.addEventListener("click", async () => {
            _currentQuantities = await _FetchQuantities(accessToken, workspaceId);
            _currentQuantitiesHtmlListElement = _RefreshUIQuantities(_currentQuantitiesHtmlListElement, _currentQuantities);
        });

        // list of quantities
        _currentQuantitiesHtmlListElement = _CreateUIQuantities(_currentQuantities);
        rootDiv.appendChild(_currentQuantitiesHtmlListElement);

        return rootDiv;
    }
    function _CreateUIQuantities(apiQuantities: IApiQuantity[] = []): HTMLUListElement {
        const oList = document.createElement("ul");

        const sortedApiQuantities: IApiQuantity[] = apiQuantities.sort(
            (apiQuantityA, apiQuantityB) => new Date(apiQuantityA.created_at).getTime() - new Date(apiQuantityB.created_at).getTime()
        );

        for (let cApiQuantity of sortedApiQuantities) {
            let cListElement: HTMLLIElement = document.createElement("li");
            cListElement.style.display = "flex";
            cListElement.style.alignItems = "center";

            let cQuantityDiv: HTMLDivElement = document.createElement("div");
            cQuantityDiv.id = cApiQuantity.id;
            cQuantityDiv.textContent = `quantity ${cApiQuantity.id} | ${cApiQuantity.type} | ${cApiQuantity.measureType} | value: ${cApiQuantity.value} | unit: ${cApiQuantity.unit} | multiplier: ${cApiQuantity.multiplier} | model: ${cApiQuantity.modelId} | versions: ${cApiQuantity.versionIds} |  created at: ${cApiQuantity.created_at}`;
            cListElement.appendChild(cQuantityDiv);
            cQuantityDiv.addEventListener("click", () => {
                // Remove bold + underline
                if (_currentSelectedQuantityHtmlDivElement) {
                    _currentSelectedQuantityHtmlDivElement.style.fontWeight = "";
                    _currentSelectedQuantityHtmlDivElement.style.textDecoration = "";
                }

                // Add bold + underline
                cQuantityDiv.style.fontWeight = "bold";
                cQuantityDiv.style.textDecoration = "underline";

                // Swap selected model
                _currentSelectedQuantityHtmlDivElement = cQuantityDiv;
            });

            // open button
            let cOpenButton : HTMLButtonElement = document.createElement( "button" );
            cOpenButton.id = "ui-quantities-open-button-" + cApiQuantity.id;
            cOpenButton.textContent = "Open";
            cOpenButton.style.marginLeft = "10px";
            cListElement.appendChild(cOpenButton);
            cOpenButton.addEventListener("click", async () => {
                if (_onQuantitySelected) {
                    _onQuantitySelected(_accessToken, cApiQuantity.workspaceId, cApiQuantity.id);
                }
            });

            // get button
            let cGetButton: HTMLButtonElement = document.createElement("button");
            cGetButton.id = "ui-quantities-get-button-" + cApiQuantity.id;
            cGetButton.textContent = "Get";
            cGetButton.style.marginLeft = "10px";
            cListElement.appendChild(cGetButton);
            cGetButton.addEventListener("click", async () => {
                const apiQuantity: IApiQuantity = await _FetchQuantity(_accessToken, cApiQuantity.workspaceId, cApiQuantity.id);
                _UpdateUIQuantitiesFromQuantity(apiQuantity);
            });

            // delete button
            let cDeleteButton: HTMLButtonElement = document.createElement("button");
            cDeleteButton.id = "ui-model-delete-button-" + cApiQuantity.id;
            cDeleteButton.textContent = "Delete";
            cDeleteButton.style.marginLeft = "10px";
            cListElement.appendChild(cDeleteButton);
            cDeleteButton.addEventListener("click", async () => {
                await _DeleteQuantity(_accessToken, cApiQuantity.workspaceId, cApiQuantity.id);
                _currentQuantities = await _FetchQuantities(_accessToken, cApiQuantity.workspaceId);
                _currentQuantitiesHtmlListElement = _RefreshUIQuantities(_currentQuantitiesHtmlListElement, _currentQuantities);
            });

            oList.appendChild(cListElement);
        }

        return oList;
    }
    function _RefreshUIQuantities(currentList?: HTMLUListElement, apiQuantities: IApiQuantity[] = []): HTMLUListElement | undefined {
        let oNextList: HTMLUListElement | undefined = undefined;

        if (currentList) {
            const nextList = _CreateUIQuantities(apiQuantities);

            const parentElement: HTMLElement | null = currentList.parentElement;
            if (parentElement) {
                parentElement.replaceChild(nextList, currentList);
                oNextList = nextList;
            }
        }

        return oNextList;
    }
    function _UpdateUIQuantitiesFromQuantity(apiQuantity: IApiQuantity): void {
        const updatedQuantities: IApiQuantity[] = _currentQuantities.map(cQuantity => (apiQuantity.id === cQuantity.id ? apiQuantity : cQuantity));
        _currentQuantitiesHtmlListElement = _RefreshUIQuantities(_currentQuantitiesHtmlListElement, updatedQuantities);

        if (_currentSelectedQuantityHtmlDivElement) {
            _currentSelectedQuantityHtmlDivElement.style.fontWeight = "";
            _currentSelectedQuantityHtmlDivElement.style.textDecoration = "";
            _currentSelectedQuantityHtmlDivElement = undefined;
        }
    }

    async function _FetchQuantities(accessToken: string, workspaceId: string): Promise<IApiQuantity[]> {
        const quantities: IApiQuantity[] = await ApiRequestHandler.GetMeQuantities(getApiKey(), accessToken, workspaceId, QuantityDataModel.EQuantityType.Measure );
        return quantities || [];
    }
    async function _FetchQuantity(accessToken: string, workspaceId: string, quantityId: string): Promise<IApiQuantity> {
        const quantity: IApiQuantity = await ApiRequestHandler.GetMeQuantity(getApiKey(), accessToken, workspaceId, quantityId, QuantityDataModel.EQuantityType.Measure);
        return quantity;
    }
    async function _DeleteQuantity(accessToken: string, workspaceId: string, quantityId: string): Promise<void> {
        await ApiRequestHandler.DeleteMeQuantity(getApiKey(), accessToken, workspaceId, quantityId, QuantityDataModel.EQuantityType.Measure );
    }
}
